import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Flag, X, Trash2, ExternalLink } from "lucide-react";

const TABS = ["open", "flagged", "closed", "all"];

export default function AdminBriefs() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "open";
  const [items, setItems] = useState([]);

  const load = () => {
    const s = status === "all" ? "" : `status=${status}`;
    api.get(`/admin/briefs?${s}`).then(({ data }) => setItems(data.items || []));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const flag = async (id) => {
    const note = window.prompt("Optional flag note:") || "";
    await api.post(`/admin/briefs/${id}/flag`, { note });
    toast.success("Flagged"); load();
  };
  const close = async (id) => { await api.post(`/admin/briefs/${id}/close`); toast.success("Closed"); load(); };
  const del = async (id) => {
    if (!window.confirm("Delete this clearance request?")) return;
    await api.delete(`/admin/briefs/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="overline text-safety">Clearance requests</div>
      <h1 className="mt-1 font-display font-black text-3xl tracking-tighter text-forest">Clearance requests</h1>

      <div className="mt-6 flex border border-border w-fit">
        {TABS.map((t) => (
          <button key={t} onClick={() => setParams({ status: t })} data-testid={`admin-briefs-tab-${t}`}
            className={`px-5 h-10 text-xs overline transition-colors duration-200 ${status === t ? "bg-forest text-white" : "bg-white hover:bg-sage"}`}>{t}</button>
        ))}
      </div>

      <div className="mt-6 border border-border bg-white">
        {items.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Nothing in "{status}"</div>
        ) : items.map((b, i) => (
          <div key={b.brief_id} data-testid={`admin-brief-row-${b.brief_id}`} className={`p-5 grid grid-cols-1 md:grid-cols-[1fr,auto] gap-4 items-start ${i > 0 ? "border-t border-border" : ""}`}>
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <Badge className={`rounded-none overline ${b.status === "flagged" ? "bg-destructive text-white" : b.status === "closed" ? "bg-sage-dark text-forest" : "bg-forest text-white"}`}>{b.status}</Badge>
                <span className="overline text-[10px] text-muted-foreground">{new Date(b.created_at).toLocaleDateString("en-GB")}</span>
              </div>
              <div className="font-display text-lg tracking-tight">{b.title}</div>
              <div className="text-xs text-muted-foreground mt-1">{b.poster_name} · {b.location} · {b.approx_items} · {b.quote_count} quotes</div>
              {b.admin_note && <div className="mt-1 text-xs text-safety">Note: {b.admin_note}</div>}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button asChild variant="outline" size="sm" className="rounded-none">
                <Link to={`/briefs/${b.brief_id}`} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-1" /> View</Link>
              </Button>
              {b.status === "open" && (
                <Button onClick={() => flag(b.brief_id)} size="sm" variant="outline" data-testid={`admin-brief-flag-${b.brief_id}`} className="rounded-none border-safety text-safety hover:bg-safety hover:text-white">
                  <Flag className="w-4 h-4 mr-1" /> Flag
                </Button>
              )}
              {b.status !== "closed" && (
                <Button onClick={() => close(b.brief_id)} size="sm" variant="outline" data-testid={`admin-brief-close-${b.brief_id}`} className="rounded-none">
                  <X className="w-4 h-4 mr-1" /> Close
                </Button>
              )}
              <Button onClick={() => del(b.brief_id)} size="sm" variant="outline" data-testid={`admin-brief-delete-${b.brief_id}`} className="rounded-none border-destructive text-destructive hover:bg-destructive hover:text-white">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
